import React, { useEffect, useMemo, useState } from 'react';
import { getDuration, useReliableInterval } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { StatusBlock } from '@/components/elements/StatusBlock';
import { convertSecondsToMinutes } from '@/utils/modifiers/convertSecondsToMinutes';

interface Props {
  rowKey: string;
  hasMfa?: boolean;
  isMfaLocked?: boolean;
  mfaLockedTo?: string;
}

const MfaLockStatus: React.FC<Props> = ({ rowKey, hasMfa = true, isMfaLocked, mfaLockedTo }) => {
  const initialSecondsLeft = useMemo(() => {
    if (!isMfaLocked || !mfaLockedTo) {
      return 0;
    }
    return dayjs(mfaLockedTo).diff(dayjs(), 'second');
  }, [isMfaLocked, mfaLockedTo]);

  const [remainingTime, setRemainingTime] = useState(initialSecondsLeft);
  const isTimerActive = isMfaLocked && remainingTime > 0;

  const { start, stop } = useReliableInterval(() => {
    setRemainingTime((value) => value - 1);
  }, getDuration({ seconds: 1 }));

  useEffect(() => {
    setRemainingTime(initialSecondsLeft);
  }, [initialSecondsLeft]);

  useEffect(() => {
    if (isTimerActive) {
      start();
      return () => {
        stop();
      };
    }
    stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTimerActive]);

  const isExpired = Boolean(mfaLockedTo) && remainingTime <= 0;

  if (!hasMfa && !isMfaLocked) {
    return (
      <StatusBlock
        testId={`user-accounts-security_item_${rowKey}_mfa-status`}
        title="None"
        backgroundColor="empty"
      />
    );
  }

  if (!isMfaLocked || isExpired) {
    return (
      <StatusBlock
        testId={`user-accounts-security_item_${rowKey}_mfa-status`}
        title="Clear"
        backgroundColor="success"
      />
    );
  }

  return (
    <StatusBlock
      testId={`user-accounts-security_item_${rowKey}_mfa-status`}
      title={mfaLockedTo ? `Locked by MFA (${convertSecondsToMinutes(remainingTime)})` : 'Locked by MFA'}
      backgroundColor="danger"
    />
  );
};

export default MfaLockStatus;
